import { useMemo, useState } from 'react';
import { BaseEdge, EdgeLabelRenderer, Position, useReactFlow, type EdgeProps } from '@xyflow/react';
import { isCompNode, isBlockNode, selectActiveNodes, useStore, type WireEdge } from '../store';
import { harnessColor } from '../harnessColor';

const EXIT_OFFSET = 30;
const STRAIGHT_TOLERANCE = 14;

const COLOR_NAMES: Record<string, string> = {
  red: '#dc2626',
  'kırmızı': '#dc2626',
  brown: '#78350f',
  'kahverengi': '#78350f',
  orange: '#f97316',
  turuncu: '#f97316',
  yellow: '#eab308',
  sarı: '#eab308',
  green: '#16a34a',
  'yeşil': '#16a34a',
  blue: '#2563eb',
  mavi: '#2563eb',
  purple: '#9333ea',
  violet: '#8b5cf6',
  mor: '#9333ea',
  white: '#ffffff',
  beyaz: '#ffffff',
  gray: '#6b7280',
  grey: '#6b7280',
  'gri': '#6b7280',
  black: '#0f172a',
  siyah: '#0f172a',
  pink: '#ec4899',
  pembe: '#ec4899',
  'green/yellow': '#65a30d',
  'yeşil/sarı': '#65a30d',
};

/** Approximate perceived brightness of a colour string (hex or named).
 *  Returns 0 (black) to 255 (white). Used to decide whether a wire needs a
 *  contrast halo behind it so the user can actually see it on the canvas. */
function computeLuma(c: string): number {
  if (!c) return 128;
  const trimmed = c.trim().toLowerCase();
  // Named colour shortcuts we resolve frequently
  if (trimmed === 'white' || trimmed === 'beyaz') return 255;
  if (trimmed === 'black' || trimmed === 'siyah') return 0;
  // Hex parsing
  let m = trimmed.match(/^#?([0-9a-f]{6})$/i);
  let rgb: [number, number, number] | null = null;
  if (m) {
    rgb = [
      parseInt(m[1].slice(0, 2), 16),
      parseInt(m[1].slice(2, 4), 16),
      parseInt(m[1].slice(4, 6), 16),
    ];
  } else {
    m = trimmed.match(/^#?([0-9a-f]{3})$/i);
    if (m) {
      rgb = [
        parseInt(m[1][0] + m[1][0], 16),
        parseInt(m[1][1] + m[1][1], 16),
        parseInt(m[1][2] + m[1][2], 16),
      ];
    }
  }
  if (!rgb) return 128;
  // Standard ITU-R BT.601 perceived-luma formula.
  return 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2];
}

function resolveWireColor(raw: string | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^#[0-9a-f]{3,8}$/i.test(trimmed)) return trimmed;
  const lower = trimmed.toLowerCase();
  return COLOR_NAMES[lower] ?? null;
}

function normalizeVoltage(v: string | undefined): string | null {
  if (!v) return null;
  const s = v.trim().toUpperCase().replace(/\s+/g, '');
  return s || null;
}

function blockHandlePosition(handleId: string | null | undefined): Position | null {
  if (handleId === 't') return Position.Top;
  if (handleId === 'r') return Position.Right;
  if (handleId === 'b') return Position.Bottom;
  if (handleId === 'l') return Position.Left;
  return null;
}

function detectMismatch(
  a: string | undefined,
  b: string | undefined,
  kindA: string | undefined,
  kindB: string | undefined,
): string | null {
  const va = normalizeVoltage(a);
  const vb = normalizeVoltage(b);
  if (va && vb && va !== vb) return `Voltage mismatch: ${a} vs ${b}`;
  // Power ↔ GND direct connection is a short
  if ((kindA === 'power' && kindB === 'gnd') || (kindA === 'gnd' && kindB === 'power')) {
    return 'Power connected directly to GND';
  }
  return null;
}

type Routing = 'hh' | 'vv' | 'hv' | 'vh' | 'dual';

interface PathResult {
  path: string;
  handle1X: number;
  handle1Y: number;
  handle2X: number;
  handle2Y: number;
  /** A point that lies ON the rendered path, used as the natural label anchor. */
  labelX: number;
  labelY: number;
  /** routing mode for the primary midpoint (controls which axes are draggable) */
  routing: Routing;
  /** whether mid2 is in use (always shown as a handle, but only matters for the path when set) */
  hasDualMid: boolean;
}

function computePath(
  sx: number,
  sy: number,
  sPos: Position,
  tx: number,
  ty: number,
  tPos: Position,
  midX?: number,
  midY?: number,
  midX2?: number,
  midY2?: number,
): PathResult {
  const sourceHorizontal = sPos === Position.Left || sPos === Position.Right;
  const targetHorizontal = tPos === Position.Left || tPos === Position.Right;

  const closeHorizontalRun =
    midX === undefined &&
    midY === undefined &&
    midX2 === undefined &&
    midY2 === undefined &&
    sourceHorizontal &&
    targetHorizontal &&
    Math.abs(sy - ty) <= STRAIGHT_TOLERANCE;
  const closeVerticalRun =
    midX === undefined &&
    midY === undefined &&
    midX2 === undefined &&
    midY2 === undefined &&
    !sourceHorizontal &&
    !targetHorizontal &&
    Math.abs(sx - tx) <= STRAIGHT_TOLERANCE;

  if (closeHorizontalRun || closeVerticalRun) {
    const path = closeHorizontalRun
      ? `M ${sx},${sy} L ${tx},${sy} L ${tx},${ty}`
      : `M ${sx},${sy} L ${sx},${ty} L ${tx},${ty}`;
    const labelX = closeHorizontalRun ? (sx + tx) / 2 : sx;
    const labelY = closeHorizontalRun ? sy : (sy + ty) / 2;
    return {
      path,
      handle1X: labelX,
      handle1Y: labelY,
      handle2X: labelX,
      handle2Y: labelY,
      labelX,
      labelY,
      routing: sourceHorizontal ? 'hh' : 'vv',
      hasDualMid: false,
    };
  }

  // Defaults for the primary midpoint (mid1) — used when user hasn't dragged it yet.
  const defaultMidX = sourceHorizontal
    ? sx + (sPos === Position.Left ? -EXIT_OFFSET : EXIT_OFFSET)
    : targetHorizontal
      ? tx + (tPos === Position.Left ? -EXIT_OFFSET : EXIT_OFFSET)
      : (sx + tx) / 2;

  const defaultMidY = !sourceHorizontal
    ? sy + (sPos === Position.Top ? -EXIT_OFFSET : EXIT_OFFSET)
    : !targetHorizontal
      ? ty + (tPos === Position.Top ? -EXIT_OFFSET : EXIT_OFFSET)
      : (sy + ty) / 2;

  const mx = midX ?? defaultMidX;
  const my = midY ?? defaultMidY;

  // Defaults for the second midpoint (mid2) — sit just outside the target.
  const defaultMid2X = targetHorizontal
    ? tx + (tPos === Position.Left ? -EXIT_OFFSET : EXIT_OFFSET)
    : sourceHorizontal
      ? sx + (sPos === Position.Left ? -EXIT_OFFSET : EXIT_OFFSET)
      : (sx + tx) / 2;
  const defaultMid2Y = !targetHorizontal
    ? ty + (tPos === Position.Top ? -EXIT_OFFSET : EXIT_OFFSET)
    : !sourceHorizontal
      ? sy + (sPos === Position.Top ? -EXIT_OFFSET : EXIT_OFFSET)
      : (sy + ty) / 2;

  const m2x = midX2 ?? defaultMid2X;
  const m2y = midY2 ?? defaultMid2Y;

  // Keep normal wires simple: one clean orthogonal bend by default.
  // A second bend is only added after the user drags the ghost handle.
  const hasDualMid = midX2 !== undefined || midY2 !== undefined;

  if (hasDualMid) {
    // Orthogonal polyline through both bend points — only 90° turns, never diagonal.
    // Each leg between waypoints is split at an intermediate corner so every
    // segment is purely horizontal or purely vertical.
    //
    // For each segment A → B:
    //   - "HV" means: A → (B.x, A.y) → B  (horizontal first, then vertical)
    //   - "VH" means: A → (A.x, B.y) → B  (vertical first, then horizontal)
    const seg1Style: 'HV' | 'VH' = sourceHorizontal ? 'HV' : 'VH';
    const seg3Style: 'HV' | 'VH' = targetHorizontal ? 'VH' : 'HV';
    // Pick seg2's style so that the corner falls on the side that produces a
    // continuous motion through M1 — i.e. mirror seg1's exit axis.
    const seg2Style: 'HV' | 'VH' = seg1Style === 'HV' ? 'VH' : 'HV';

    const ortho = (
      ax: number,
      ay: number,
      bx: number,
      by: number,
      style: 'HV' | 'VH',
    ) =>
      style === 'HV'
        ? `L ${bx},${ay} L ${bx},${by}`
        : `L ${ax},${by} L ${bx},${by}`;

    const path =
      `M ${sx},${sy} ` +
      `${ortho(sx, sy, mx, my, seg1Style)} ` +
      `${ortho(mx, my, m2x, m2y, seg2Style)} ` +
      `${ortho(m2x, m2y, tx, ty, seg3Style)}`;

    // The corner of the M1 → M2 leg is always a real point on the rendered
    // path (since seg2 is orthogonal and bends through that corner). We use it
    // as the label anchor so labels never float off the wire.
    const labelX = seg2Style === 'HV' ? m2x : mx;
    const labelY = seg2Style === 'HV' ? my : m2y;

    return {
      path,
      handle1X: mx,
      handle1Y: my,
      handle2X: m2x,
      handle2Y: m2y,
      labelX,
      labelY,
      routing: 'dual',
      hasDualMid: true,
    };
  }

  // Single-midpoint orthogonal routing (existing behavior).
  let routing: Routing;
  let path: string;
  let handle1X = mx;
  let handle1Y = my;

  if (sourceHorizontal && targetHorizontal) {
    path = `M ${sx},${sy} L ${mx},${sy} L ${mx},${ty} L ${tx},${ty}`;
    handle1X = mx;
    handle1Y = (sy + ty) / 2;
    routing = 'hh';
  } else if (!sourceHorizontal && !targetHorizontal) {
    path = `M ${sx},${sy} L ${sx},${my} L ${tx},${my} L ${tx},${ty}`;
    handle1X = (sx + tx) / 2;
    handle1Y = my;
    routing = 'vv';
  } else if (sourceHorizontal && !targetHorizontal) {
    path = `M ${sx},${sy} L ${mx},${sy} L ${mx},${my} L ${tx},${my} L ${tx},${ty}`;
    handle1X = mx;
    handle1Y = my;
    routing = 'hv';
  } else {
    path = `M ${sx},${sy} L ${sx},${my} L ${mx},${my} L ${mx},${ty} L ${tx},${ty}`;
    handle1X = mx;
    handle1Y = my;
    routing = 'vh';
  }
  return {
    path,
    handle1X,
    handle1Y,
    handle2X: m2x,
    handle2Y: m2y,
    labelX: handle1X,
    labelY: handle1Y,
    routing,
    hasDualMid: false,
  };
}

export function AdjustableEdge(props: EdgeProps<WireEdge>) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    style,
    markerEnd,
    selected,
  } = props;

  const { screenToFlowPosition } = useReactFlow();
  const updateEdgeData = useStore((s) => s.updateEdgeData);
  const nodes = useStore(selectActiveNodes);
  const [dragPatch, setDragPatch] = useState<Partial<NonNullable<typeof data>> | null>(null);
  const effectiveData = dragPatch ? { ...data, ...dragPatch } : data;

  // Look up pins on both ends for voltage/kind validation.
  const srcNode = nodes.find((n) => n.id === props.source);
  const tgtNode = nodes.find((n) => n.id === props.target);
  const srcPin =
    srcNode && isCompNode(srcNode)
      ? srcNode.data.pins.find((p) => p.id === props.sourceHandleId)
      : undefined;
  const tgtPin =
    tgtNode && isCompNode(tgtNode)
      ? tgtNode.data.pins.find((p) => p.id === props.targetHandleId)
      : undefined;

  const mismatch = detectMismatch(srcPin?.voltage, tgtPin?.voltage, srcPin?.kind, tgtPin?.kind);
  const effectiveSourcePosition =
    srcNode && isBlockNode(srcNode)
      ? blockHandlePosition(props.sourceHandleId) ?? sourcePosition
      : sourcePosition;
  const effectiveTargetPosition =
    tgtNode && isBlockNode(tgtNode)
      ? blockHandlePosition(props.targetHandleId) ?? targetPosition
      : targetPosition;

  // Block-diagram edges (block↔block) represent an entire harness as a
  // single line. The label is automatically the harness name — manual label
  // edits are intentionally suppressed here so the block diagram stays a
  // direct mirror of the harness inventory.
  const isBlockEdge = !!srcNode && !!tgtNode && isBlockNode(srcNode) && isBlockNode(tgtNode);
  const sheets = useStore((s) => s.sheets);
  const harnessName = useMemo(() => {
    if (!effectiveData?.harnessId) return null;
    for (const sh of sheets) {
      if (sh.kind === 'interconnect' && sh.bundles) {
        const b = sh.bundles.find((bb) => bb.id === effectiveData.harnessId);
        if (b) return b.name || '(unnamed)';
      }
    }
    return null;
  }, [sheets, effectiveData?.harnessId]);
  // Resolve every merged harness id (only set when the simplified-block
  // view collapsed several edges into this one) to a name.
  const mergedHarnessNames = useMemo(() => {
    const ids = effectiveData?.mergedHarnessIds;
    if (!ids || ids.length <= 1) return null;
    const byId = new Map<string, string>();
    for (const sh of sheets) {
      if (sh.kind === 'interconnect' && sh.bundles) {
        for (const b of sh.bundles) byId.set(b.id, b.name || '(unnamed)');
      }
    }
    return ids.map((id) => byId.get(id) ?? '(?)');
  }, [sheets, effectiveData?.mergedHarnessIds]);
  const focusHarness = useStore((s) => s.focusHarness);

  const {
    path,
    handle1X,
    handle1Y,
    handle2X,
    handle2Y,
    labelX: pathLabelX,
    labelY: pathLabelY,
    routing,
    hasDualMid,
  } = computePath(
    sourceX,
    sourceY,
    effectiveSourcePosition,
    targetX,
    targetY,
    effectiveTargetPosition,
    effectiveData?.midX,
    effectiveData?.midY,
    effectiveData?.midX2,
    effectiveData?.midY2,
  );

  // For the primary midpoint:
  // - In dual mode, it can move freely in both axes.
  // - In single-mid orthogonal modes, it follows the clean segment axis.
  const axisX = hasDualMid || routing !== 'vv';
  const axisY = hasDualMid || routing !== 'hh';

  const cursor1 = axisX && axisY ? 'move' : axisX ? 'ew-resize' : 'ns-resize';

  const onMid1Down = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    let latest: { midX?: number; midY?: number } = {};
    const onMove = (ev: PointerEvent) => {
      const pt = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      const patch: { midX?: number; midY?: number } = {};
      if (axisX) patch.midX = pt.x;
      if (axisY) patch.midY = pt.y;
      latest = patch;
      setDragPatch((prev) => ({ ...(prev ?? {}), ...patch }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setDragPatch(null);
      updateEdgeData(id, latest);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onMid2Down = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    let latest: { midX2?: number; midY2?: number } = {};
    const onMove = (ev: PointerEvent) => {
      const pt = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      // Dragging the second handle activates dual-mid routing — set both axes.
      latest = { midX2: pt.x, midY2: pt.y };
      setDragPatch((prev) => ({ ...(prev ?? {}), ...latest }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setDragPatch(null);
      updateEdgeData(id, latest);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onResetMid2 = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateEdgeData(id, { midX2: undefined, midY2: undefined });
  };

  const onResetMid = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateEdgeData(id, { midX: undefined, midY: undefined });
  };

  // Labels are anchored to the corner between the two bend handles, which is
  // always a real point on the orthogonal wire path. No freeform dragging —
  // labels stay on the line at all times.
  let labelX = pathLabelX;
  let labelY = pathLabelY;
  // De-pile labels on dense parallel bundles: when many near-straight wires run
  // side by side (e.g. an 8-wire fan from a connector), their midpoints cluster
  // and the labels stack on top of each other. Spread each wire's label to a
  // different point ALONG its own straight run based on the wire's row/column,
  // so neighbours cascade in a readable staircase instead of overlapping. The
  // label stays exactly on its wire because the run is straight here.
  if (!isBlockEdge && !hasDualMid) {
    const SLOTS = 5;
    const horizontalRun = routing === 'hh' && Math.abs(sourceY - targetY) <= STRAIGHT_TOLERANCE;
    const verticalRun = routing === 'vv' && Math.abs(sourceX - targetX) <= STRAIGHT_TOLERANCE;
    if (horizontalRun) {
      const row = Math.round(sourceY / 11);
      const slot = ((row % SLOTS) + SLOTS) % SLOTS;
      const t = 0.22 + (slot / (SLOTS - 1)) * 0.56; // 0.22 .. 0.78 along the run
      labelX = sourceX + (targetX - sourceX) * t;
      labelY = sourceY;
    } else if (verticalRun) {
      const col = Math.round(sourceX / 11);
      const slot = ((col % SLOTS) + SLOTS) % SLOTS;
      const t = 0.22 + (slot / (SLOTS - 1)) * 0.56;
      labelY = sourceY + (targetY - sourceY) * t;
      labelX = sourceX;
    }
  }

  const userColor = resolveWireColor(effectiveData?.color);

  // "Focus mode": every wire lights up amber + thick when it's part of the
  // focused subject and fades into the background otherwise. Two subjects:
  //   • selecting a component → its wires light up
  //   • focusing a harness    → wires assigned to that harness light up
  // Component focus has priority when both are active simultaneously.
  const focusedNodeId = useStore((s) => s.selectedNodeId);
  const focusedHarnessId = useStore((s) => s.focusedHarnessId);
  const focusedNet = useStore((s) => s.focusedNet);
  const focusedNetMode = useStore((s) => s.focusedNetMode);
  const nodeFocus = focusedNodeId !== null;
  const harnessFocus = focusedHarnessId !== null;
  const netFocus = focusedNet !== null;
  const focusActive = nodeFocus || harnessFocus || netFocus;
  const isOnNodePath =
    nodeFocus &&
    (props.source === focusedNodeId || props.target === focusedNodeId);
  const isOnHarnessPath =
    harnessFocus && effectiveData?.harnessId === focusedHarnessId;
  const isOnNetPath = netFocus && focusedNet!.edgeIds.includes(props.id);
  // priority: component selection → harness → net
  const isOnFocusPath = nodeFocus
    ? isOnNodePath
    : harnessFocus
      ? isOnHarnessPath
      : netFocus
        ? isOnNetPath
        : false;
  const focusFade = focusActive && !isOnFocusPath ? 0.12 : 1;
  const hiddenByNetSolo = netFocus && focusedNetMode === 'solo' && !isOnNetPath;
  // NOTE: the power-wire filter is applied in Canvas (via edge.hidden) where
  // node pin kinds are available — power-ness lives in pin kinds + labels, not
  // only in an explicit netName, so it can't be decided from edge data here.

  // Block-diagram edges with a harness assignment paint in the harness's
  // stable colour so multiple branches of the same harness (e.g. one DB25
  // trunk fanning out to relay + ethernet + motor) all read as ONE physical
  // harness at a glance. Schematic wires keep their own colour rules.
  const harnessLineColor =
    isBlockEdge && effectiveData?.harnessId ? harnessColor(effectiveData.harnessId) : null;

  const strokeColor = mismatch
    ? '#dc2626'
    : selected
      ? '#2563eb'
      : isOnFocusPath
        ? '#2f81f7'
        : harnessLineColor ??
          userColor ??
          ((style?.stroke as string | undefined) ?? '#64748b');
  const baseStrokeWidth =
    typeof effectiveData?.strokeWidth === 'number' && effectiveData.strokeWidth > 0
      ? effectiveData.strokeWidth
      : isBlockEdge && effectiveData?.harnessId
        ? 2
        : ((style?.strokeWidth as number | undefined) ?? 1.5);
  const strokeWidth =
    selected || mismatch || isOnFocusPath
      ? Math.max(baseStrokeWidth + 1, 2.5)
      : baseStrokeWidth;

  // Contrast halo: a faint dark stroke rendered UNDER the real wire so that
  // white / very-light wires stay visible on the always-white drawing sheet.
  // Subtle for normal-coloured wires, strong for near-white ones.
  const haloColor = '#000000';
  const haloLuma = computeLuma(strokeColor);
  const haloOpacity = haloLuma >= 220 ? 0.85 : 0.14;

  if (hiddenByNetSolo) return null;

  return (
    <>
      {/* Contrast halo — keeps black-on-dark and white-on-light wires
          visible. Drawn first so the real wire renders on top. */}
      <path
        d={path}
        stroke={haloColor}
        strokeWidth={strokeWidth + 2}
        fill="none"
        opacity={haloOpacity * focusFade}
        style={{ pointerEvents: 'none' }}
      />
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: strokeColor,
          strokeWidth,
          fill: 'none',
          opacity: focusFade,
        }}
      />
      {/* Two-tone wire: a dashed overlay in the second colour over the base
          stroke reads as a striped conductor (e.g. orange/white). */}
      {!isBlockEdge && effectiveData?.color2 && !selected && !mismatch && (
        <path
          d={path}
          fill="none"
          stroke={resolveWireColor(effectiveData.color2) ?? effectiveData.color2}
          strokeWidth={strokeWidth}
          strokeDasharray="7 7"
          style={{ pointerEvents: 'none' }}
          opacity={focusFade}
        />
      )}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={18}
        className="nodrag nopan"
        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          updateEdgeData(id, {
            midX: undefined,
            midY: undefined,
            midX2: undefined,
            midY2: undefined,
          });
        }}
      />

      {/* Midpoint drag handles — visible only when the edge is selected */}
      {selected && (
        <>
          <EdgeLabelRenderer>
            <div
              onPointerDown={onMid1Down}
              onDoubleClick={onResetMid}
              className="nodrag nopan"
              title={
                axisX && axisY
                  ? 'Drag bend 1 · double-click to reset'
                  : axisX
                    ? 'Drag bend 1 left/right · double-click to reset'
                    : 'Drag bend 1 up/down · double-click to reset'
              }
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${handle1X}px, ${handle1Y}px)`,
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: 'var(--accent)',
                border: '2px solid white',
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.45)',
                cursor: cursor1,
                pointerEvents: 'all',
                zIndex: 5,
              }}
            />
          </EdgeLabelRenderer>
          {/* Second midpoint handle — drag to add a second bend (full freedom) */}
          <EdgeLabelRenderer>
            <div
              onPointerDown={onMid2Down}
              onDoubleClick={onResetMid2}
              className="nodrag nopan"
              title={
                hasDualMid
                  ? 'Drag bend 2 · double-click to remove second bend'
                  : 'Drag to add a second bend point'
              }
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${handle2X}px, ${handle2Y}px)`,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: hasDualMid ? '#2563eb' : '#94a3b8',
                border: hasDualMid ? '2px solid white' : '2px dashed #cbd5e1',
                boxShadow: hasDualMid
                  ? '0 2px 6px rgba(37, 99, 235, 0.45)'
                  : '0 1px 3px rgba(0, 0, 0, 0.2)',
                cursor: 'move',
                pointerEvents: 'all',
                zIndex: 5,
                opacity: hasDualMid ? 1 : 0.6,
              }}
            />
          </EdgeLabelRenderer>
        </>
      )}

      {mismatch && (
        <EdgeLabelRenderer>
          <div
            title={mismatch}
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY - 18}px)`,
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              borderRadius: 999,
              padding: '1px 7px',
              fontSize: 10,
              fontWeight: 700,
              pointerEvents: 'all',
              whiteSpace: 'nowrap',
            }}
          >
            ⚠ {mismatch}
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Label — anchored to the wire. For block-diagram edges the label is
          AUTOMATICALLY the harness name (clickable: jumps to the schematic
          and lights every wire of that harness). Schematic / generic edges
          show the user-typed label text. */}
      {isBlockEdge && (mergedHarnessNames || effectiveData?.harnessId) ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            onClick={(e) => {
              e.stopPropagation();
              if (effectiveData?.harnessId) focusHarness(effectiveData.harnessId);
            }}
            title={
              mergedHarnessNames
                ? `${mergedHarnessNames.length} harnesses merged in simplified view:\n  • ${mergedHarnessNames.join('\n  • ')}`
                : effectiveData?.harnessId
                  ? `Harness: ${harnessName} — click to highlight in schematic`
                  : ''
            }
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              cursor: effectiveData?.harnessId ? 'pointer' : 'default',
              background: harnessLineColor
                ? `${harnessLineColor}1f`
                : 'var(--panel)',
              border: harnessLineColor
                ? `1px solid ${harnessLineColor}`
                : '1px dashed var(--panel-border)',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 11,
              fontWeight: 700,
              color: harnessLineColor ?? 'var(--text-muted)',
              boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              fontFamily: 'ui-monospace, Consolas, monospace',
              lineHeight: 1.2,
              opacity: focusFade,
              // Keep labels above component nodes (the edgelabel-renderer layer
              // sits below nodes by default, so they'd hide behind a block).
              zIndex: 8,
            }}
          >
            {mergedHarnessNames
              ? mergedHarnessNames.length <= 3
                ? mergedHarnessNames.join(' · ')
                : `${mergedHarnessNames.length} harnesses`
              : effectiveData?.harnessId
                ? harnessName ?? '(missing harness)'
                : ''}
          </div>
        </EdgeLabelRenderer>
      ) : null}

      {/* Schematic / non-block edge label — wire no + net name + gauge. */}
      {!isBlockEdge &&
        (effectiveData?.wireNo ||
          effectiveData?.netName ||
          effectiveData?.label ||
          effectiveData?.gauge) && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            title="Wire — number / net / gauge (edit in the Connection list or Inspector)"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
              background: 'var(--panel)',
              border: '1px solid var(--panel-border)',
              borderRadius: 4,
              padding: '1px 5px',
              fontSize: 10,
              fontWeight: 500,
              color: 'var(--text)',
              boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              fontFamily: 'ui-monospace, Consolas, monospace',
              lineHeight: 1.2,
              opacity: focusFade,
              // Keep the wire label above component nodes — by default the
              // edgelabel-renderer layer paints below nodes, so the label
              // would slip behind a block (as reported).
              zIndex: 8,
            }}
          >
            {effectiveData.wireNo && (
              <span style={{ color: 'var(--accent)', marginRight: 4, fontWeight: 800 }}>
                {effectiveData.wireNo}
              </span>
            )}
            {effectiveData.netName || effectiveData.label || ''}
            {effectiveData.gauge && (
              <span style={{ color: 'var(--text-muted)', marginLeft: 4, fontSize: 10 }}>
                {effectiveData.gauge}
              </span>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
